/**
 * The settings form itself, with no opinion about what is around it.
 *
 * It used to be welded into the pause screen, which is why Esc was the only way
 * to reach it. It is a panel now so that every route into it - the front page,
 * the pause menu, the finish screen - shows the same one.
 *
 * Two clearly separated groups: the dose (changes the game and the leaderboard
 * you are on) and comfort settings (change nothing that can be raced).
 */

import { INTOXICATION_LIMITS } from '../config/intoxication';
import { MAX_NAME_LENGTH } from '../config/leaderboard';
import { SEED_SLIDER_MAX, SETTINGS_LIMITS, type GameSettings } from '../config/settings';
import { MAX_SEED, parseSeed } from '../sim/seed';
import { sanitisePlayerName } from '../score/playerName';
import { el } from './dom';
import type { SettingsStore } from './SettingsStore';

export interface SeedSetting {
  value: number;
  change: (seed: number) => void;
}

interface Limits {
  min: number;
  max: number;
  step: number;
}

export class SettingsPanel {
  readonly root = el('div', 'settings');
  /** Validate and apply the draft only when the user confirms the settings. */
  applySeed: () => boolean = () => true;

  constructor(private readonly settings: SettingsStore, seed: SeedSetting) {
    this.root.append(
      this.group(
        'Nimi tulostaulussa',
        'Ilman nimeä yritys ajetaan silti - se ei vain päädy jaettuun tulostauluun.',
        this.nameRow(),
      ),
      this.group(
        'Kenttä',
        'Seed 0 käyttää alkuperäistä kenttää. Vaihto aloittaa pelin alusta, kun painat Valmis. ' +
          'Voit myös syöttää tarkan numeron. Jaa kenttä sivun osoitteella.',
        this.seedRow(seed),
      ),
      this.group(
        'Kunto',
        'Muuttaa peliä: yritys alkaa alusta ja ennätykset ovat annoskohtaisia. ' +
          'Oletuksena selvin päin - nosta lukemia sitten kun tiedät mihin olet menossa.',
        this.numericRow('Promillet', 'alcoholPromille', INTOXICATION_LIMITS.alcoholPromille, 2, '‰'),
        this.numericRow('Muut päihteet', 'drugsPercent', INTOXICATION_LIMITS.drugsPercent, 0, '%'),
      ),
      this.group(
        'Näyttö ja ohjaus',
        'Pelkkiä mukavuusasetuksia - kameran heilunta ei vaikuta liikkeeseen. ' +
          'Hiiren herkkyys lukitaan yrityksen ajaksi (muutos puree seuraavaan).',
        this.slider('Hiiren herkkyys', 'mouseSensitivity', SETTINGS_LIMITS.mouseSensitivity, (v) =>
          v.toFixed(2),
        ),
        this.toggle('Käänteinen hiiren pystysuunta', 'invertMouse'),
        this.slider(
          'Kameran heilunta',
          'cameraSwayIntensity',
          SETTINGS_LIMITS.cameraSwayIntensity,
          (v) => `${Math.round(v * 100)}%`,
        ),
        this.slider('Näkökenttä (FOV)', 'fieldOfView', SETTINGS_LIMITS.fieldOfView, (v) =>
          String(Math.round(v)),
        ),
        this.toggle('Äänet', 'audioEnabled'),
        this.toggle('Colliderit (F4)', 'showColliders'),
      ),
    );
  }

  private seedRow(selection: SeedSetting): HTMLElement {
    const row = el('div', 'settings__row');
    const range = el('input');
    range.type = 'range';
    range.id = 'level-seed';
    range.min = '0';
    // Keep existing competition links usable even outside the usual slider range.
    range.max = String(Math.max(SEED_SLIDER_MAX, selection.value));
    range.step = '1';
    range.value = String(selection.value);

    const input = el('input');
    input.type = 'number';
    input.min = '0';
    input.max = String(MAX_SEED);
    input.step = '1';
    input.value = String(selection.value);
    input.setAttribute('aria-label', 'Seed numerona');
    let selected = selection.value;
    const commit = (): boolean => {
      const seed = parseSeed(input.value.trim());
      if (seed === null) {
        input.setCustomValidity(`Syötä kokonaisluku 0–${MAX_SEED}.`);
        input.reportValidity();
        return false;
      }
      input.setCustomValidity('');
      range.max = String(Math.max(SEED_SLIDER_MAX, seed));
      range.value = input.value = String(seed);
      selected = seed;
      return true;
    };
    this.applySeed = () => {
      if (!commit()) return false;
      if (selected !== selection.value) selection.change(selected);
      return true;
    };
    range.addEventListener('input', () => {
      input.value = range.value;
      input.setCustomValidity('');
    });
    // Editing updates the draft; only Valmis applies it to the game.
    range.addEventListener('change', commit);
    input.addEventListener('input', () => input.setCustomValidity(''));
    input.addEventListener('change', commit);
    input.addEventListener('blur', commit);
    for (const control of [range, input]) {
      control.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          range.value = input.value = String(selected);
          input.setCustomValidity('');
          return;
        }
        event.stopPropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
      });
      control.addEventListener('keyup', (event) => event.stopPropagation());
    }
    row.addEventListener('mousedown', (event) => event.stopPropagation());
    const label = el('label', undefined, 'Seed');
    label.htmlFor = range.id;
    const value = el('div', 'settings__value');
    value.append(input);
    row.append(label, range, value);
    return row;
  }

  private group(title: string, note: string, ...rows: HTMLElement[]): HTMLElement {
    const group = el('section', 'settings__group');
    group.append(el('h3', 'settings__title', title), el('p', 'settings__note', note), ...rows);
    return group;
  }

  /**
   * The display name. Sanitised on the way in so what you see here is what the
   * board will show - the server sanitises again, and that is the one that
   * counts, but silently mangling a name after the fact is worse than showing
   * the real rules while it is being typed.
   */
  private nameRow(): HTMLElement {
    const row = el('div', 'settings__row');
    const input = el('input');
    input.type = 'text';
    input.maxLength = MAX_NAME_LENGTH;
    input.placeholder = 'Näkyy tulostaulussa';
    input.value = this.settings.values.playerName;
    input.autocomplete = 'off';
    input.spellcheck = false;

    const commit = (): void => {
      const raw = input.value.trim();
      // An empty box means "no name", not the fallback: the player has simply
      // not chosen yet, and the finish screen should say so.
      const value = raw.length === 0 ? '' : sanitisePlayerName(raw);
      input.value = value;
      if (value !== this.settings.values.playerName) this.settings.update({ playerName: value });
    };

    input.addEventListener('change', commit);
    input.addEventListener('blur', commit);
    // R and E are game keys the moment they leave this box; Enter would start a
    // run. Nothing typed in here belongs to the game.
    input.addEventListener('keydown', (event) => {
      event.stopPropagation();
      if (event.key === 'Enter') input.blur();
    });

    this.settings.subscribe((values) => {
      if (document.activeElement !== input) input.value = values.playerName;
    });

    row.append(el('label', undefined, 'Nimimerkki'), input);
    return row;
  }

  /**
   * Slider plus an exact number box, so 0.01 promille is actually reachable.
   * The unit sits outside the box rather than inside its value, because a
   * `number` input will not accept a suffix and typing one would blank the row.
   */
  private numericRow(
    label: string,
    key: keyof GameSettings,
    limits: Limits,
    decimals: number,
    unit: string,
  ): HTMLElement {
    const row = el('div', 'settings__row');

    const range = el('input');
    range.type = 'range';
    range.min = String(limits.min);
    range.max = String(limits.max);
    range.step = String(limits.step);
    range.value = String(this.settings.values[key]);

    const number = el('input');
    number.type = 'number';
    number.min = String(limits.min);
    number.max = String(limits.max);
    number.step = String(limits.step);
    number.value = Number(this.settings.values[key]).toFixed(decimals);

    const snap = (raw: number): number => {
      const clamped = Math.min(limits.max, Math.max(limits.min, raw));
      return Number((Math.round(clamped / limits.step) * limits.step).toFixed(decimals));
    };

    const commit = (raw: number, source: 'range' | 'number'): void => {
      if (!Number.isFinite(raw)) return;
      const value = snap(raw);
      if (source !== 'range') range.value = String(value);
      if (source !== 'number') number.value = value.toFixed(decimals);
      this.settings.update({ [key]: value } as Partial<GameSettings>);
    };

    // The dose is the one setting whose change restarts the run, and a drag
    // fires `input` for every step the handle crosses. Committing there restarted
    // the run - and re-requested the ticket and the board - for every one of
    // them, and each restart stopped the ready screen's snoring loop and started
    // a fresh copy over the ones still fading out. Half a second of dragging
    // stacked up dozens of them, which is what the crackle was.
    //
    // So the box follows the handle, and the dose lands when the handle is let
    // go. `change` covers every way a range is committed: the drag ending, an
    // arrow key, a click on the track.
    range.addEventListener('input', () => {
      number.value = snap(Number(range.value)).toFixed(decimals);
    });
    range.addEventListener('change', () => commit(Number(range.value), 'range'));
    number.addEventListener('change', () => commit(Number(number.value), 'number'));

    this.settings.subscribe((values) => {
      const value = Number(values[key]);
      if (document.activeElement !== range) range.value = String(value);
      if (document.activeElement !== number) number.value = value.toFixed(decimals);
    });

    // The unit rides with the box rather than sitting in the row's own 16px
    // gap, where it would read as a column of its own instead of a suffix.
    const box = el('div', 'settings__value');
    box.append(number, el('span', 'settings__unit', unit));

    row.append(el('label', undefined, label), range, box);
    return row;
  }

  private slider(
    label: string,
    key: keyof GameSettings,
    limits: Limits,
    format: (value: number) => string,
  ): HTMLElement {
    const row = el('div', 'settings__row');
    const input = el('input');
    input.type = 'range';
    input.min = String(limits.min);
    input.max = String(limits.max);
    input.step = String(limits.step);
    input.value = String(this.settings.values[key]);

    const output = el('output', undefined, format(Number(input.value)));
    input.addEventListener('input', () => {
      const value = Number(input.value);
      output.textContent = format(value);
      this.settings.update({ [key]: value } as Partial<GameSettings>);
    });

    row.append(el('label', undefined, label), input, output);
    return row;
  }

  private toggle(label: string, key: keyof GameSettings): HTMLElement {
    const row = el('div', 'settings__row settings__row--toggle');
    const input = el('input');
    input.type = 'checkbox';
    input.checked = Boolean(this.settings.values[key]);
    input.addEventListener('change', () => {
      this.settings.update({ [key]: input.checked } as Partial<GameSettings>);
    });
    // Keep the checkbox in sync when the same value is toggled with F4.
    this.settings.subscribe((values) => {
      input.checked = Boolean(values[key]);
    });
    row.append(el('label', undefined, label), input);
    return row;
  }
}
